import { Alert, Flex } from '@mantine/core';
import { useState } from 'react';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	useVestingTermsContext,
	VestingTermsProvider,
} from '@dealstack/frontend/shared/components/VestingTerms/VestingTermsContext';
import { EditableTabs } from '@dealstack/frontend/shared/components/EditableTabs/EditableTabs';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer';
import { TotalVestingAllocation } from '@dealstack/frontend/shared/components/VestingTerms/TotalVestingAllocation/TotalVestingAllocation.tsx';
import { VestingModeRadio } from '@dealstack/frontend/shared/components/VestingTerms/VestingModeRadio/VestingModeRadio.tsx';
import { TimeBasedVesting } from '@dealstack/frontend/shared/components/VestingTerms/TimeBasedVesting/TimeBasedVesting.tsx';
import { LeaverProvisionsWrapper } from '@dealstack/frontend/shared/components/VestingTerms/LeaverProvision/LeaverProvisionsWrapper.tsx';
import { VestingTermsFormStyles } from '@dealstack/frontend/shared/components/VestingTerms/VestingTerms.styles.tsx';
import { PerformanceVesting } from '@dealstack/frontend/shared/components/VestingTerms/PerformanceVesting/PerformanceVesting.tsx';
import { VestingTermRes } from '@dealstack/shared/lib/generated';

interface Props {
	skipDeleteConfirmation?: boolean;
	organizationId?: string;
}

export const VestingTerms = ({ skipDeleteConfirmation, organizationId }: Props) => {
	return (
		<VestingTermsProvider organizationId={organizationId}>
			<VestingTermsContent skipDeleteConfirmation={skipDeleteConfirmation} />
		</VestingTermsProvider>
	);
};

const VestingTermsContent = ({ skipDeleteConfirmation }: { skipDeleteConfirmation?: boolean }) => {
	const tCommonUi = useTranslationCommonUi();

	const {
		vestingTerms,
		currentVestingTerm,
		deleteLoading,
		isPerformanceVestingAvailable,
		setCurrentVestingTermId,
		onCreateVestingTerm,
		onDeleteVestingTerm,
		onTabNameChange,
	} = useVestingTermsContext();

	const [activeEdit, setActiveEdit] = useState<boolean>(false);
	const [selectedDeleteTerm, setSelectedDeleteTerm] = useState<VestingTermRes>();

	const handleTabNameChange = (id: string, name: string) => {
		onTabNameChange(id, name);
		setActiveEdit(false);
	};

	// With no terms the selected term is the placeholder, whose empty id every edit would post.
	const hasSelectedTerm = !!currentVestingTerm.id;

	const VestingTermsForm = (
		<>
			{currentVestingTerm.hasLiveTransactions && (
				<Alert color="orange" mb="0.5rem">
					{tCommonUi('vesting_terms.vesting_mode.change_warning')}
				</Alert>
			)}
			<VestingTermsFormStyles>
				<Flex direction="column" gap="0.5rem" className="col">
					<VestingModeRadio />

					<TotalVestingAllocation
						timeBasedPercentage={currentVestingTerm.timeBasedPercentage}
						performancePercentage={currentVestingTerm.performancePercentage}
					/>

					<TimeBasedVesting />

					{isPerformanceVestingAvailable && <PerformanceVesting />}
				</Flex>

				<div className="col">
					<LeaverProvisionsWrapper />
				</div>
			</VestingTermsFormStyles>
		</>
	);

	return (
		<>
			<EditableTabs
				newButtonText={tCommonUi('vesting_terms.add_vesting_term')}
				deleteButtonText={tCommonUi('vesting_terms.remove_vesting_term')}
				editInputPlaceholder={tCommonUi('vesting_terms.add_vesting_term_placeholder')}
				tabs={vestingTerms ?? []}
				activeTab={currentVestingTerm.id}
				setActiveTab={setCurrentVestingTermId}
				addNewTab={onCreateVestingTerm}
				activeEdit={activeEdit}
				setActiveEdit={setActiveEdit}
				onTabNameChange={handleTabNameChange}
				onRemoveTab={(termId) =>
					skipDeleteConfirmation
						? onDeleteVestingTerm(termId)
						: setSelectedDeleteTerm(vestingTerms?.find((t) => t.id === termId))
				}
				children={hasSelectedTerm ? VestingTermsForm : null}
			/>

			{!skipDeleteConfirmation && (
				<ConfirmDeleteDrawer
					opened={!!selectedDeleteTerm}
					onClose={() => setSelectedDeleteTerm(undefined)}
					title={tCommonUi('vesting_terms.delete_drawer.title')}
					alertTitle={tCommonUi('vesting_terms.delete_drawer.alertTitle')}
					alertDescription={tCommonUi('vesting_terms.delete_drawer.alertDescription')}
					inputLabel={tCommonUi('vesting_terms.delete_drawer.inputLabel')}
					inputPlaceholder={tCommonUi('vesting_terms.delete_drawer.inputPlaceholder')}
					hideAlertIcon={true}
					confirmDeleteText={selectedDeleteTerm?.name || 'delete'}
					wordMismatchErrorMessage={tCommonUi(
						'vesting_terms.delete_drawer.wordMismatchError'
					)}
					deleteBtnText={tCommonUi('vesting_terms.delete_drawer.confirmDelete')}
					cancelBtnText={tCommonUi('vesting_terms.delete_drawer.cancel')}
					deleteLoading={deleteLoading}
					handleDelete={() => {
						onDeleteVestingTerm(selectedDeleteTerm?.id || '');
						setSelectedDeleteTerm(undefined);
					}}
				/>
			)}
		</>
	);
};
